import { getRarity, type RarityTier } from '../../utils/rarityCalculations';

/** Visual treatment for one rarity tier: card gradient (160deg, from → to) and the text colour on it. */
export interface UtTier {
  tier: RarityTier;
  label: string;
  /** Short rule shown in the tier legend. */
  rule: string;
  from: string;
  to: string;
  ink: string;
}

/**
 * Tier colours from the UT v2 handoff (docs/design/ut-v2/README.md).
 * Tier membership is decided in the database by calculate_player_rarity()
 * (docs/RarityTierSystem.md); this only maps the stored value to colours.
 * Academy (0 XP, 0 caps) isn't in the design, so it gets a teal to match the
 * classic PlayerCard's Academy treatment.
 */
export const UT_TIERS: Record<RarityTier, UtTier> = {
  Legendary: { tier: 'Legendary', label: 'Legendary', rule: 'Top 2%', from: '#f7de8e', to: '#b0842a', ink: '#1a1204' },
  'World Class': { tier: 'World Class', label: 'World Class', rule: 'Top 7%', from: '#c9a6ff', to: '#5526b0', ink: '#ffffff' },
  Professional: { tier: 'Professional', label: 'Professional', rule: 'Top 20%', from: '#86c6ff', to: '#1a55b3', ink: '#ffffff' },
  'Semi Pro': { tier: 'Semi Pro', label: 'Semi Pro', rule: 'Top 40%', from: '#8aebb6', to: '#17804f', ink: '#04140c' },
  Amateur: { tier: 'Amateur', label: 'Amateur', rule: 'XP > 0', from: '#d7dae0', to: '#7b808c', ink: '#111318' },
  Academy: { tier: 'Academy', label: 'Academy', rule: 'New, 0 caps', from: '#8fe3dc', to: '#1f7f86', ink: '#04140c' },
  Retired: { tier: 'Retired', label: 'Retired', rule: 'No games in 40', from: '#3b3e46', to: '#0c0d10', ink: '#e8e8e8' },
};

/** Legend order, best first. */
export const UT_TIER_ORDER: RarityTier[] = ['Legendary', 'World Class', 'Professional', 'Semi Pro', 'Amateur', 'Academy', 'Retired'];

/** Resolve a DB rarity string (player_xp.rarity) plus XP to its UT tier. Unknown strings fall back to Amateur. */
export function utTier(rarity: string | null | undefined, xp?: number | null): UtTier {
  const t = getRarity(rarity ?? null, xp ?? null);
  return UT_TIERS[t] ?? UT_TIERS.Amateur;
}

/** CSS background for a tier gradient. */
export function tierGradient(t: UtTier, angle = 160): string {
  return `linear-gradient(${angle}deg, ${t.from}, ${t.to})`;
}

/** True when a hex colour is dark (relative luminance under 0.4). */
export function isDarkColour(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return l < 0.4;
}

/**
 * A soft glow behind card text in the tier's own colour, so dark (or light) text
 * stays readable where it crosses a kit-portrait cut-out (a beard, a shirt).
 */
export function textHalo(t: UtTier): string {
  const c = isDarkColour(t.ink) ? t.from : t.to;
  // Subtle: just enough to lift the letters off a beard or shirt (owner: "a tad too intense" at 3/6/10px solid).
  return `0 0 2px ${c}cc, 0 0 5px ${c}80`;
}

/**
 * Behind the name and stats when a kit-portrait cut-out runs to the bottom of the
 * card: a plate that fades up from the bottom in the tier's own colour — its light
 * end behind dark ink (gold, green, silver), its dark end behind light ink (purple,
 * blue, retired) — so the text reads while the shoulders still show through.
 */
export function cutoutPlate(t: UtTier, strength = 0.92): string {
  const c = isDarkColour(t.ink) ? t.from : t.to;
  const a = (x: number) => Math.round(Math.max(0, Math.min(1, x)) * 255).toString(16).padStart(2, '0');
  return `linear-gradient(to top, ${c}${a(strength)} 0%, ${c}${a(strength * 0.85)} 45%, ${c}00 100%)`;
}
